/* eslint-disable unicorn/filename-case */

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react';

import { evaluationDetailsQueryOptions } from '@/api/evaluationDetails';
import { selfEvaluationQueryOptions } from '@/api/selfEvaluation';
import { EvaluationForm } from '@/components/evaluation/evaluationForm';
import { Button } from '@/components/input/button';
import { useEvaluationTargets } from '@/hooks/useEvaluationTargets';
import { EvaluationSearchSchema } from '@/schemas/evaluationSearch';
import { targetHeaderOf } from '@/util/evaluationTargets';
import { isNotFoundError } from '@/util/httpError';
import { deadlineOf } from '@/util/todoItems';

export const Route = createFileRoute('/dashboard/_authenticated/evaluation-self')({
  component: RouteComponent,
  validateSearch: EvaluationSearchSchema,
  staticData: {
    pageWidth: 'wide',
    breadcrumb: ['Evaluations'],
  },
})

function RouteComponent() {
  const { form } = Route.useSearch();
  const evaluations = useEvaluationTargets();
  const [now] = useState(() => new Date());
  const selfTarget = evaluations.targets.find((target) => target.isSelf);
  const {
    data: selfEvaluation,
    error: selfEvaluationError,
    isLoading: isLoadingSelfEvaluation,
    refetch: refetchSelfEvaluation,
  } = useQuery(selfEvaluationQueryOptions());
  const {
    data: evaluationDetails,
    error: detailsError,
    isLoading: isLoadingDetails,
    refetch: refetchDetails,
  } = useQuery(evaluationDetailsQueryOptions(selfEvaluation?.evaluationId));

  if (isLoadingSelfEvaluation || isLoadingDetails) {
    return <div className="text-body2 text-neutral-600">Loading...</div>;
  }

  if (isNotFoundError(selfEvaluationError)) {
    return <div className="text-body2 text-neutral-600">You have no self-evaluation to fill right now.</div>;
  }

  if (selfEvaluationError || detailsError || !evaluationDetails) {
    return (
      <div className="flex flex-col items-start gap-3">
        <div className="text-body2 text-error-300">Couldn't load your self-evaluation.</div>
        <Button
          onClick={() => {
            void refetchSelfEvaluation();
            void refetchDetails();
          }}
          size="sm"
          type="button"
        >
          Retry
        </Button>
      </div>
    );
  }

  const { campaign, evaluation } = evaluationDetails;
  const header = selfTarget
    ? targetHeaderOf(selfTarget, campaign)
    : { breadcrumbLabel: "Self-evaluation", title: "Self-evaluation", subtitle: campaign.name };

  return (
    <EvaluationForm
      breadcrumbLabel={header.breadcrumbLabel}
      deadline={deadlineOf(campaign.endsAt, now)}
      breadcrumbPath="/dashboard/evaluation-self"
      campaignName={campaign.name}
      evaluationId={evaluation.id}
      headerSubtitle={header.subtitle}
      headerTitle={header.title}
      initialFormId={form}
      items={evaluation.commentList}
      key={evaluation.id}
      readOnly={evaluation.status !== "OPEN" || campaign.isLocked}
    />
  );
}
